import { useCallback, useEffect, useRef, useState } from 'react';
import { routes } from '~/constants';
import { useOverlayContext } from '~/contexts';
import { useBiometricMethodLabel } from '~/hooks/useBiometricMethodLabel';
import { makeOAuthStateStorage } from '~/hooks/useNativeOAuthSignIn/oauthStateStorage';
import { useLocaleKeys } from '~/i18n';
import { useIoContext } from '~/providers/IoContext';
export const useBiometricLogin = (errorToastTestId)=>{
    const { native, cookies, localStorage, browser: { changeLocation, scrubUrlFragment } } = useIoContext();
    const { showErrorToast } = useOverlayContext();
    const { t: loginTexts } = useLocaleKeys('pages.auth.login');
    const method = useBiometricMethodLabel();
    const enabled = native.isNative;
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const inFlight = useRef(false);
    const signIn = useCallback(async ()=>{
        if (inFlight.current) {
            return;
        }
        inFlight.current = true;
        setLoading(true);
        const result = await native.biometric.restore();
        if (result.status === 'unusable') {
            await native.biometric.disable();
            setVisible(false);
            showErrorToast(loginTexts.biometricResetToast({
                method
            }), errorToastTestId);
        }
        if (result.status !== 'restored') {
            inFlight.current = false;
            setLoading(false);
            return;
        }
        cookies.setAuthTokenForNative(result.token, {
            skipBiometricMarkerClear: true
        });
        await native.biometric.markRestored();
        scrubUrlFragment();
        changeLocation(routes.home);
    }, [
        errorToastTestId,
        method
    ]);
    useEffect(()=>{
        if (!enabled) {
            return undefined;
        }
        let cancelled = false;
        let unsubscribe;
        const init = async ()=>{
            const [{ isAvailable }, enrolled] = await Promise.all([
                native.biometric.getBiometryStatus(),
                native.biometric.isEnrolled()
            ]);
            if (cancelled) {
                return;
            }
            setVisible(isAvailable && enrolled);
            unsubscribe = await native.biometric.onBiometryChange(async (available)=>{
                const stillEnrolled = await native.biometric.isEnrolled();
                if (!cancelled) {
                    setVisible(available && stillEnrolled);
                }
            });
            const justLoggedOut = await native.session.getJustLoggedOut();
            const oauthPending = makeOAuthStateStorage(localStorage).isAnyPending();
            if (isAvailable && enrolled && !justLoggedOut && !oauthPending && !cancelled) {
                void signIn();
            }
        };
        void init();
        return ()=>{
            cancelled = true;
            unsubscribe === null || unsubscribe === void 0 ? void 0 : unsubscribe();
        };
    // eslint-disable-next-line react-hooks-static-deps/exhaustive-deps
    }, [
        enabled
    ]);
    return {
        visible,
        loading,
        method,
        signIn
    };
};
